import { TFCartContent } from '~graphql/generated/clientGraphql'
import { sortArray } from '~logic/helpers/arrayUtils/sort'
import { TGroupedItem } from '~logic/helpers/groupUtils/groupItems'

type TOrderItems = TFCartContent['items']
type TItem = TGroupedItem<TOrderItems[number]>
type TItemPieces = TItem['items']

const ARRAY_START_INDEX = 0

/**
 * Sorts the order items by the origin (~the place from where the item was added).
 * @see {@link sortItemsByOrigin} for details
 */
export const sortOrderItems = (items: TOrderItems) => {
  return sortArray(sortItemsByOrigin)(items)
}

type TGetItemsToRemoveOptions = {
  items: TItemPieces
  removedItemCount: number
}

/**
 * Get the first `removedItemCount` item instances from the grouped item.
 *
 * _items should have been sorted before grouping by their origin (~the place from where the item was added)_
 * @see {@link sortItemsByOrigin} for details
 */
export const getItemsToRemove = ({ items, removedItemCount }: TGetItemsToRemoveOptions) => {
  return items.splice(ARRAY_START_INDEX, removedItemCount)
}

//* HELPERS
/**
 * Returns if the item piece was added from the cart (has no `promoTag` && no `productCategory.id`).
 */
const wasAddedViaCart = (piece: TItemPieces[number]) =>
  !piece.promoTag && !piece.productCategory?.id

/**
 * Returns if the item piece was added from the product list (has no `promoTag`).
 */
const wasAddedFromProductList = (piece: TItemPieces[number]) => !piece.promoTag

/**
 * Sorts the items in the following order:
 *
 * `wasAddedViaCart` < `wasAddedFromProductList` < other (added from promo categories) items
 *
 * This should reflect the priority we give to the different origins (promo categories, product list, cart controls) in terms of statistics:
 *  - the highest priority should have the products added from the promo categories *as we primarily want to monitor the impact of the promo categories*,
 *  - the lowest priority should be given to products added via the cart controls (plus button, edit cart item modal).
 *
 * Priority plays the role in removing the items later as the items are being removed from the first (least important) one.
 */
const sortItemsByOrigin = (itemA: TItemPieces[number], itemB: TItemPieces[number]) => {
  if (wasAddedViaCart(itemA)) {
    return wasAddedViaCart(itemB) ? 0 : -1
  }

  if (wasAddedFromProductList(itemA)) {
    if (wasAddedViaCart(itemB)) {
      return 1
    }
    return wasAddedFromProductList(itemB) ? 0 : -1
  }

  return wasAddedViaCart(itemB) || wasAddedFromProductList(itemB) ? 1 : 0
}
